import { ReactNode, cloneElement, isValidElement } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { Card } from '@/components/ui';

interface DraggableWidgetProps {
  id: string;
  children: ReactNode;
  isDragDisabled?: boolean;
  className?: string;
  noPadding?: boolean;
}

export interface DragHandleProps {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  isDragDisabled: boolean;
}

export function DraggableWidget({
  id,
  children,
  isDragDisabled = false,
  className = '',
  noPadding = false,
}: DraggableWidgetProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Pass drag handle props to children
  const childrenWithProps = isValidElement(children)
    ? cloneElement(children, {
        dragHandleProps: { attributes, listeners, isDragDisabled },
      } as { dragHandleProps: DragHandleProps })
    : children;

  return (
    <div ref={setNodeRef} style={style} className={`relative ${className}`}>
      <Card className={`overflow-visible ${isDragging ? 'shadow-2xl' : ''}`} noPadding={noPadding}>
        {childrenWithProps}
      </Card>
    </div>
  );
}

// Reusable DragHandle component that widgets can use
export function DragHandle({ attributes, listeners, isDragDisabled }: DragHandleProps) {
  if (isDragDisabled) return null;

  return (
    <div
      {...attributes}
      {...listeners}
      className="hidden cursor-move rounded-lg bg-gray-100 p-2 transition-colors hover:bg-gray-200 dark:bg-dark-700 dark:hover:bg-dark-600 md:block"
      title="Drag to reorder"
    >
      <GripVertical className="h-4 w-4 text-gray-500 dark:text-gray-400" />
    </div>
  );
}
